import React from 'react';
import { useSelector } from 'react-redux';
import Alert from 'housecall-ui/dist/DesignSystem/Feedback/Alert/Alert';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import HousecallThemeProvider from 'housecall-ui/dist/DesignSystem/Styles/HousecallThemeProvider';
import { AmplitudeFlagKeys } from 'types/amplitudeFlags';

import useIsExperimentActive from 'components/Amplitude/useIsExperimentActive';
import BannerBar, { ERROR_BAR } from 'components/Common/NoticeBar/BannerBar';

import { getDowngradeFeatureMessages } from './Redux/Selectors';
import { usePayWallDialogGridPaddingFixStyles, usePayWallDialogMainGridStyles } from './PayWallDialog';
import PayWallEditableAddress from './PayWallEditableAddress';
import PayWallPaymentInformation from './PayWallPaymentInformation';
import PayWallPlanSummary from './PayWallPlanSummary';
import PayWallPlanSummaryV2 from './PayWallPlanSummaryV2';

type Props = {
  stripeErrorMessage: string | undefined;
};

export default function PayWallPaymentReview({ stripeErrorMessage }: Props): JSX.Element {
  const isNewSummaryActive = useIsExperimentActive(AmplitudeFlagKeys.billingNewPayWallPlanSummary);

  const gridClasses = usePayWallDialogMainGridStyles();
  const gridPaddingFixClasses = usePayWallDialogGridPaddingFixStyles();
  const downgradeFeatureMessages = useSelector(getDowngradeFeatureMessages);

  return (
    <div id="paywall-review-dialog" data-testid="paywall-review">
      {stripeErrorMessage && (
        <Box display="flex" width={1} mb={2} data-testid="stripe-error-message">
          <HousecallThemeProvider>
            <BannerBar type={ERROR_BAR} message={stripeErrorMessage} hasHeader />
          </HousecallThemeProvider>
        </Box>
      )}

      {downgradeFeatureMessages.length > 0 && (
        <>
          {downgradeFeatureMessages.map((downgradingFeature) => (
            <Box display="flex" width={1} mb={2} data-testid={`${downgradingFeature.name}-downgrade-message`}>
              <Alert severity="warning">{downgradingFeature.message}</Alert>
            </Box>
          ))}
        </>
      )}

      <Grid container spacing={5} classes={gridClasses}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6} classes={gridPaddingFixClasses}>
          <Grid container direction="column">
            <Grid item>
              <PayWallPaymentInformation />
              <PayWallEditableAddress />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6} classes={gridPaddingFixClasses}>
          {isNewSummaryActive ? <PayWallPlanSummaryV2 /> : <PayWallPlanSummary />}
        </Grid>
      </Grid>
    </div>
  );
}
